import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  ChevronDown,
  ChevronUp,
  Shield,
  X,
} from "lucide-react";

import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { CountryCell } from "../../components/CountryCell/CountryCell";
import { Pill } from "../../components/Pill/Pill";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../lib/errors";
import { formatMessageTime, isMemberEvent } from "../guilds/chatFeedFormat";
import { MemberEventRow } from "../guilds/MemberEventRow";
import btn from "../../components/Button/Button.module.css";
import feed from "../guilds/GuildChatFeed.module.css";
import { getContext } from "./api";
import { contextQueryKey } from "./queryKeys";
import type { ContextMessage } from "./types";
import styles from "./ChatContextPanel.module.css";

/** Messages shown on each side of the selected one, and added per click. */
const CONTEXT_STEP = 6;
const POLL_MS = 15_000;

interface Props {
  projectId: string;
  messageId: string;
  /** The message's `created_at`, when the stream has its row. */
  createdAt?: string;
  onClose: () => void;
  /** Told once the panel has found the message deleted. */
  onGone?: (messageId: string) => void;
  /** Moves the stream's selection; null where there is no row to move to. */
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
}

/**
 * The docked context panel: the selected message among its neighbours in its
 * own guild, bots, system lines and member events included. Read-only.
 *
 * Mount it with `key={messageId}` so the loaded depth starts over per message.
 */
export function ChatContextPanel({
  projectId,
  messageId,
  createdAt,
  onClose,
  onGone,
  onPrev,
  onNext,
}: Props) {
  const [before, setBefore] = useState(CONTEXT_STEP);
  const [after, setAfter] = useState(CONTEXT_STEP);

  const query = useQuery({
    queryKey: contextQueryKey(projectId, messageId, before, after),
    queryFn: () => getContext(projectId, messageId, before, after, createdAt),
    refetchInterval: POLL_MS,
    placeholderData: keepPreviousData,
    retry: (count, err) =>
      !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const gone = query.error instanceof ApiError && query.error.status === 404;
  const data = query.data;

  useEffect(() => {
    if (gone && data) onGone?.(messageId);
  }, [gone, data, messageId, onGone]);

  return (
    <aside className={styles.panel} aria-label="Message context">
      <header className={styles.head}>
        <div className={styles.headMain}>
          <span className={styles.tile}>
            <Shield size={17} aria-hidden="true" />
          </span>
          <div className={styles.titles}>
            {data ? (
              <>
                <h4 className={styles.title} title={data.guild.name}>
                  {data.guild.name}
                </h4>
                <div className={styles.sub}>
                  {data.guild.member_count.toLocaleString()}{" "}
                  {data.guild.member_count === 1 ? "member" : "members"}
                </div>
              </>
            ) : (
              <>
                <Skeleton width={120} height={13} />
                <Skeleton width={70} height={10} />
              </>
            )}
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="icon"
            className={styles.iconBtn}
            aria-label="Previous message"
            disabled={!onPrev}
            onClick={() => onPrev?.()}
          >
            <ChevronUp size={15} aria-hidden="true" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className={styles.iconBtn}
            aria-label="Next message"
            disabled={!onNext}
            onClick={() => onNext?.()}
          >
            <ChevronDown size={15} aria-hidden="true" />
          </Button>
          <span className={styles.vsep} />
          {data && (
            <Link
              to={`/projects/${projectId}/guilds/${data.guild.id}`}
              className={`${btn.btn} ${btn.secondary} ${btn.sm}`}
            >
              Open guild <ArrowRight size={13} aria-hidden="true" />
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={styles.iconBtn}
            aria-label="Close"
            onClick={onClose}
          >
            <X size={16} aria-hidden="true" />
          </Button>
        </div>
      </header>

      {data?.author && (
        <div className={styles.player}>
          <div className={styles.who}>
            <Avatar name={data.author.display_name} size={26} />
            <div className={styles.whoText}>
              <div className={styles.whoName}>{data.author.display_name}</div>
              <div className={styles.whoSub}>
                <span className={styles.extId} title={data.author.external_id}>
                  {data.author.external_id}
                </span>
                {data.author.country && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className={styles.country}>
                      <CountryCell code={data.author.country} />
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <Link
            to={`/projects/${projectId}/players/${data.author.id}`}
            className={`${btn.btn} ${btn.ghost} ${btn.sm} ${styles.playerLink}`}
          >
            View player <ArrowRight size={13} aria-hidden="true" />
          </Link>
        </div>
      )}

      <div className={styles.body}>
        {!data && query.isPending && (
          <div className={feed.chatFeed}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={feed.msg}>
                <Skeleton width={30} height={30} radius={999} />
                <div className={styles.skelLines}>
                  <Skeleton width="35%" height={11} />
                  <Skeleton width="80%" height={11} />
                </div>
              </div>
            ))}
          </div>
        )}
        {!data && query.isError && (
          <div className={styles.error} role="alert">
            {gone
              ? "This message is no longer in the chat history."
              : describeError(
                  query.error instanceof ApiError
                    ? query.error.code
                    : "internal",
                )}
          </div>
        )}
        {data && (
          <>
            {data.has_earlier && (
              <div className={feed.chatOlder}>
                <button
                  type="button"
                  className={feed.link}
                  disabled={query.isFetching}
                  onClick={() => setBefore((n) => n + CONTEXT_STEP)}
                >
                  Load earlier
                </button>
              </div>
            )}
            <div className={feed.chatFeed}>
              {data.items.map((m) => (
                <ContextRow
                  key={m.id}
                  msg={m}
                  focused={m.id === data.message_id}
                  deleted={gone && m.id === data.message_id}
                />
              ))}
            </div>
            {data.has_later ? (
              <div className={`${feed.chatOlder} ${styles.later}`}>
                <button
                  type="button"
                  className={feed.link}
                  disabled={query.isFetching}
                  onClick={() => setAfter((n) => n + CONTEXT_STEP)}
                >
                  Load later
                </button>
              </div>
            ) : (
              <div className={`${feed.chatAnchor} ${styles.anchor}`}>
                Newest message in the guild
              </div>
            )}
          </>
        )}
      </div>

      <footer className={styles.foot}>
        <span className={styles.note}>
          <span className={styles.kbd}>↑</span>
          <span className={styles.kbd}>↓</span> or{" "}
          <span className={styles.kbd}>j</span>
          <span className={styles.kbd}>k</span> step through the stream ·{" "}
          <span className={styles.kbd}>Esc</span> close · bots, system messages
          and member events dimmed
        </span>
      </footer>
    </aside>
  );
}

/** One line of the context feed, dimmed unless a living player wrote it. */
function ContextRow({
  msg,
  focused,
  deleted,
}: {
  msg: ContextMessage;
  focused: boolean;
  deleted: boolean;
}) {
  if (isMemberEvent(msg)) {
    return <MemberEventRow msg={msg} className={styles.dim} />;
  }
  if (msg.kind === "system") {
    return (
      <div className={`${feed.sysMsg} ${styles.dim}`}>
        <span className={feed.sysInner}>
          <span className={feed.sysTag}>System</span>
          <span>{msg.body}</span>
        </span>
      </div>
    );
  }
  const name = msg.sender_name ?? "?";
  const cls = [
    feed.msg,
    msg.sender_is_bot && !focused ? styles.dim : "",
    focused ? styles.focus : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      className={cls}
      data-testid={focused ? "context-focus" : undefined}
      aria-current={focused ? "true" : undefined}
    >
      <span className={feed.avSm}>
        <Avatar name={name} size={30} />
      </span>
      <div className={feed.msgMain}>
        <div className={feed.msgHead}>
          <span className={feed.msgName}>{name}</span>
          {msg.sender_is_bot && (
            <span className={styles.pillWrap}>
              <Pill size="sm">
                <span className={feed.botMark}>
                  <Bot size={10} aria-hidden="true" />
                  Bot
                </span>
              </Pill>
            </span>
          )}
          <span className={feed.msgTime}>
            {formatMessageTime(msg.created_at)}
          </span>
        </div>
        {deleted ? (
          <div className={`${feed.msgBody} ${styles.deleted}`}>
            This message was deleted
          </div>
        ) : (
          <div className={feed.msgBody}>{msg.body}</div>
        )}
      </div>
    </div>
  );
}
